import isEqual from 'lodash/isEqual';
import shuffle from 'lodash/shuffle';
import type { Asset, SafeLuminary, Scene, Template, TemplateColor } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';
import labPlugin from 'colord/plugins/lab';
import { permutations } from 'combinatorial-generators';
import type { BrandKitColor } from 'js/src/client';
import type { ColorShuffleInfo } from 'js/src/data/color-shuffle';
import { cosine_similarity } from 'js/src/libs/array-utils';
import { TEXT_HIGHLIGHT_PROP } from 'js/src/luminary';
import { calculateLuminance } from 'js/src/utils/contrast-utils';

// Extend colord to have delta() and luminance() methods
extend([labPlugin, a11yPlugin]);

export const MAX_COLOR_SHUFFLES = 6;
export const MAX_TEMPLATE_COLOR_SHUFFLE_PERMUTATIONS = 8;

export function intToHex(val: number): string {
    return `#${val.toString(16).padStart(6, '0')}`;
}

// How light a colour has to be to read as white against a plain white surface. #FAFAFA scores
// ~0.96, so near-white tints count while off-whites that are still visibly tinted do not.
const WHITE_LUMINANCE_THRESHOLD = 0.95;

/**
 * Whether a Luminary colour integer is white — or light enough that, against white, its edges are
 * invisible and whatever frames it needs a contrasting backdrop to be seen.
 */
export function isWhiteColor(color: number): boolean {
    return (
        calculateLuminance({ r: (color >> 16) & 0xff, g: (color >> 8) & 0xff, b: color & 0xff }) >=
        WHITE_LUMINANCE_THRESHOLD
    );
}

export function hexToInt(hex: string): number {
    const cleaned = hex.replace('#', '');
    return parseInt(cleaned.substring(0, 6), 16);
}

/**
 * Parse an agent-supplied hex color into the integer Luminary stores. Accepts an optional leading
 * `#` and either the six-digit form (`#ff0000`) or the three-digit CSS shorthand (`#f00`, expanded
 * to `#ff0000`). Returns null for anything else, so the color-setting tools can reject bad input
 * with a corrective error instead of writing garbage. The single input parser shared by every color
 * tool.
 */
export function parseHexColor(input: string): number | null {
    if (typeof input !== 'string') {
        return null;
    }
    const cleaned = input.replace(/^#/, '');
    if (/^[0-9a-fA-F]{3}$/.test(cleaned)) {
        // Expand shorthand the way CSS does: each digit is doubled (#f00 → #ff0000).
        const expanded = cleaned.replace(/./g, (digit) => digit + digit);
        return parseInt(expanded, 16);
    }
    if (/^[0-9a-fA-F]{6}$/.test(cleaned)) {
        return parseInt(cleaned, 16);
    }
    return null;
}

export const getColorDistanceMatrices = (colorPermutations: number[][]) => {
    return colorPermutations.map((permutation) => {
        // This matrix will contain the contrast of each pair of colors
        // we store the matrix as a flat list for simplicity.
        const matrix = [];
        // Build a matrix where i is a color, j is a color and the value is
        // the contrast of both colors.
        for (let i = 0; i < permutation.length; i++) {
            for (let j = 0; j < permutation.length; j++) {
                const c1 = intToHex(permutation[i]);
                const c2 = intToHex(permutation[j]);

                // deltaE (luminance + chroma)
                const contrast = colord(c1).delta(c2);
                matrix.push(contrast);
            }
        }

        return matrix;
    });
};

/**
 * Find the best color palette permutations that best preserves the original contrast ratios
 */
export const generateBestColorPalettePermutationsFromLuminary = ({
    luminary,
}: {
    luminary: SafeLuminary;
}): number[][] => {
    const initialLuminaryColors = luminary.template.colors.map((color: TemplateColor) => {
        return color.color;
    });

    const defaultColorDistanceMatrix = getColorDistanceMatrices([initialLuminaryColors])[0];

    const MAX_PERMUTATIONS = 100000;
    const colorPermutations = [];
    const generator = permutations(initialLuminaryColors, initialLuminaryColors.length);

    while (colorPermutations.length < MAX_PERMUTATIONS) {
        const next = generator.next();
        if (next?.value) {
            colorPermutations.push(next.value);
        }
        if (next?.done) {
            break;
        }
    }

    let colorPermutationsSubset = [];

    // If the template has many colors, we need to limit the number of permutations where we run our color matrix code.
    // So let's pick a subset of the permutations. There should be enough good palettes in 100 randomly picked permutations.
    // It's a compromise between number of palettes we analize and the time it takes to do the first shuffle.
    const SUBSET_OF_COLOR_PERMUTATIONS = 100;
    colorPermutationsSubset = shuffle(colorPermutations).slice(0, SUBSET_OF_COLOR_PERMUTATIONS);

    // Find contrast distances that best match the original template colors
    const palettes = colorPermutationsSubset.map((palette) => {
        const matrix = getColorDistanceMatrices([palette])[0];
        const distance = defaultColorDistanceMatrix.reduce((acc, contrast, index) => {
            return acc + Math.abs(contrast - matrix[index]);
        }, 0);
        return {
            distance,
            palette,
        };
    });

    palettes.sort((a, b) => {
        return a.distance - b.distance;
    });

    const arePalletesTooSimilar = (palette1: number[], palette2: number[]) => {
        const THRESHOLD = 0.1 * palette1.length;
        let totalContrast = 0;
        for (let colorIndex = 0; colorIndex < palette1.length; colorIndex++) {
            const c1 = intToHex(palette1[colorIndex]);
            const c2 = intToHex(palette2[colorIndex]);
            totalContrast += colord(c1).delta(c2);
        }
        return totalContrast < THRESHOLD;
    };

    // The next step is pretty slow, so we only keep the best 150 palettes
    // Note that this is not so useful right now except for templates with lots of colors.
    // This filtering will also be a critical part of the algorithm if we feed in more user brand kit colors in
    // addition to template colors. (which is something we might or might not do)
    const bestPermutations = palettes.slice(0, 150);

    // Figure out if some palettes are too similar
    for (let i = 0; i < bestPermutations.length; i++) {
        for (let j = i + 1; j < bestPermutations.length; j++) {
            if (arePalletesTooSimilar(bestPermutations[i].palette, bestPermutations[j].palette)) {
                bestPermutations.splice(j, 1);
                j--;
            }
        }
    }

    return bestPermutations
        .slice(0, MAX_TEMPLATE_COLOR_SHUFFLE_PERMUTATIONS)
        .map((entry) => entry.palette);
};

class Color {
    // Provides functions for converting between integer and rgb color formats and calculating other color metrics

    /**
     * Returns decimal equivalent to given rgb color values
     */
    static rgbToDecimal(r: number, g: number, b: number): number {
        return (r << 16) + (g << 8) + b;
    }

    /**
     * Convert an integer-encoded color to an RGB Array
     */
    static rgb(val: number): number[] {
        const r = (val >> 16) & 255;
        const g = (val >> 8) & 255;
        const b = val & 255;
        return [r, g, b];
    }

    static luminance(rgb: number[]): number {
        // https://stackoverflow.com/questions/596216/formula-to-determine-perceived-brightness-of-rgb-color
        const a = rgb.map((v) => {
            v /= 255;
            return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
        });
        return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
    }

    static rgbContrast(rgb1: number[], rgb2: number[]): number {
        // calculate the contrast between two RGB colors using their luminance
        const lum1 = Color.luminance(rgb1);
        const lum2 = Color.luminance(rgb2);
        const brightest = Math.max(lum1, lum2);
        const darkest = Math.min(lum1, lum2);
        return (brightest + 0.05) / (darkest + 0.05);
    }
}

class ColorPalette {
    colors: readonly TemplateColor[];
    colorNames: string[];
    luminanceOrder: Record<string, number>;

    /* A simple wrapper around template.colors that adds some utility functions
     *
     * @param colors - an instance of template.colors
     */
    constructor(colors: readonly TemplateColor[]) {
        this.colors = colors;
        // these are the names that will appear as values for color properties
        this.colorNames = [...Array(this.colors.length).keys()].map((i) => 'colors_' + i);

        // create a mapping from color name to relative luminance, where a mapping of
        // { color_0: 0, color_1: 2, color_2: 1 } means color_0 is the darkest color, color_2 is the lightest color,
        // and color_1 is somewhere in the middle
        const colorLuminance = this.colors.map((color, i) => {
            return {
                colorName: 'colors_' + i,
                luminance_val: Color.luminance(Color.rgb(color.color)),
            };
        });

        const luminanceSortFn = (a: { luminance_val: number }, b: { luminance_val: number }) => {
            if (a.luminance_val === b.luminance_val) {
                return 0;
            } else if (a.luminance_val < b.luminance_val) {
                return -1;
            } else {
                return 1;
            }
        };

        colorLuminance.sort(luminanceSortFn);
        // map color names to the luminance position (greater means brighter)
        this.luminanceOrder = {};
        colorLuminance.forEach((c, i) => {
            this.luminanceOrder[c.colorName] = i;
        });
    }

    findDuplicateColors() {
        // finds all pairs of color palette colors that are duplicates (i.e. they're the same color)
        // Returns a set of "<color 1 palette index> <color 2 palette index>" strings
        const colorIndices: Record<number, number[]> = {};
        const duplicateColorIndices = new Set<string>();

        this.colors.forEach((color, i) => {
            colorIndices[color.color] = colorIndices[color.color] || [];

            if (colorIndices[color.color].length > 0) {
                colorIndices[color.color].forEach((j) => {
                    duplicateColorIndices.add(`${j} ${i}`);
                    duplicateColorIndices.add(`${i} ${j}`);
                });
            }
            colorIndices[color.color].push(i);
        });

        return duplicateColorIndices;
    }
}

class DuotoneFilter {
    /**
     * Duotone filters are assets that have two colors. One of those color properties MUST be mapped to a color that is
     * darker than the other color. This class encapsulates attributes and logic to help us validate
     * whether a specific permutation of a color palette preserves the light-to-dark ordering of the filter's color attributes
     */
    props: { propName: string; propValue: string }[];
    darkestPropName: string | null;
    darkestLuminance: number | null;
    darkestColorReference: string | null;

    constructor() {
        this.props = [];
        this.darkestPropName = null;
        this.darkestLuminance = null;
        this.darkestColorReference = null;
    }

    /**
     * Add a new color property to the filter, possibly updating the darkest color attributes
     */
    addColorProp(propName: string, propValue: string, color: number) {
        this.props.push({ propName, propValue });

        const propLuminance = Color.luminance(Color.rgb(color));

        if (this.darkestLuminance === null || propLuminance < this.darkestLuminance) {
            // this is the name of the property that MUST ALWAYS be mapped to the darkest color
            this.darkestPropName = propName;
            // this tells us which position in the permutation we need to check to see which new color would be the darkest
            // color if we use a particular color permutation
            this.darkestColorReference = propValue;
            // this just lets us track the darkest color among the properties we've added to this filter so far
            this.darkestLuminance = propLuminance;
        }
    }

    /**
     * Does a given color palette permutation preserve the color luminance constraints of this filter?
     */
    validateColorPermutation(
        luminanceOrder: Record<string, number>,
        permutedPaletteIndices: number[],
        colorNames: string[],
    ) {
        // Which color is now in the position of the darkest color in the filter?
        if (this.darkestColorReference === null) throw new Error('darkestColorReference is null');
        const newDarkestColorIndex =
            permutedPaletteIndices[colorNames.indexOf(this.darkestColorReference)];
        // What's the luminance of the new darkest color?
        const newDarkestColorName = colorNames[newDarkestColorIndex];
        // how dark is the darkest color?
        const darkestLuminancePos = luminanceOrder[newDarkestColorName];

        // Figure out which color would be mapped to every property of the filter asset, and make sure the new
        // assignments don't cause the filter colors to have inverted luminance
        const prop_statuses = this.props
            .filter((prop) => prop.propName !== this.darkestPropName)
            .map((prop) => {
                // What color would be assigned to this prop if we use this permutation?
                const newColorIndex = permutedPaletteIndices[colorNames.indexOf(prop.propValue)];
                const newColorName = colorNames[newColorIndex];

                // the color mapped to this property must not be darker than the color we've said must be the darkest color
                if (luminanceOrder[newColorName] <= darkestLuminancePos) {
                    return false;
                }
                return true;
            });

        // if every prop passed the test above, the filter is valid
        return prop_statuses.every((v) => v === true);
    }
}

export function colorSwapperByLuminance(
    originalColors: readonly TemplateColor[],
    newColors: BrandKitColor[],
    isDuplicationAllowed: boolean,
    appendUnusedNewColors = false,
): TemplateColor[] {
    const originalColorsLuminance: {
        index: number;
        luminance: number;
        color: number;
        alphaEditable: boolean;
    }[] = [];
    const newColorsLuminance: {
        index: number;
        luminance: number;
        color: number;
        alpha: number;
        isSelected: boolean;
    }[] = [];
    const closestMatchingColors: TemplateColor[] = [];

    originalColors.forEach((originalColorDetails, index) => {
        const originalColorInt = originalColorDetails.color;
        const originalColorRgb = Color.rgb(originalColorInt);
        originalColorsLuminance.push({
            index,
            luminance: Color.luminance(originalColorRgb),
            alphaEditable: originalColorDetails.alphaEditable,
            color: originalColorInt,
        });
    });
    newColors.forEach((newColor, index) => {
        const newColorRgb = [newColor.r, newColor.g, newColor.b];
        newColorsLuminance.push({
            index,
            luminance: Color.luminance(newColorRgb),
            color: Color.rgbToDecimal(newColorRgb[0], newColorRgb[1], newColorRgb[2]),
            alpha: newColor.a / 100,
            isSelected: false,
        });
    });
    originalColorsLuminance.forEach((color) => {
        const closest = newColorsLuminance
            .filter((color) => color.isSelected === false)
            .reduce(function (prev, curr) {
                return Math.abs(curr.luminance - color.luminance) <
                    Math.abs(prev.luminance - color.luminance)
                    ? curr
                    : prev;
            });
        closestMatchingColors.push({
            color: closest.color,
            alpha: closest.alpha,
            alphaEditable: color.alphaEditable,
        });
        if (!isDuplicationAllowed) {
            closest.isSelected = true;
        }
    });

    // Append any unused new colors if requested
    if (appendUnusedNewColors) {
        const unusedColors = newColorsLuminance
            .filter((color) => !color.isSelected)
            .map((color) => ({
                color: color.color,
                alpha: color.alpha,
                alphaEditable: true,
            }));
        closestMatchingColors.push(...unusedColors);
    }

    return closestMatchingColors;
}

/* Does the slide have a background color? If so, what color palette index is it?
 * @return an integer if a background color is found or null otherwise
 */
export function getBackgroundColor(
    slide: Scene,
    slideArea: number,
    colorNames: string[],
): number | null {
    const templateReferenceMap = slide.buildTemplateReferenceMap();
    const children = slide.getAssets().filter((c: Asset) => c.name in templateReferenceMap);

    let backgroundColor = null;
    // we'll only consider the first color asset we find
    let seenColorAsset = false;
    // assume only one full-bleed user-editable media asset
    let seenFullscreenEditableMedia = false;
    // as we iterate through slide children, keep the area covered by the best candidate background color
    let bestColorArea = 0;

    /* General strategy: find the color of the asset that takes up the most slide area while not being hidden underneath
    a subsequent asset. For example, if there's a full-screen, user-editable asset on top of a color_1 asset, color_1's color
    won't be the background color, but if there's no user-editable asset on top of color_1, color_1 could very well be the background
    color */
    for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (
            (VideoData.isMediaAsset(child) || VideoData.isColorAsset(child)) &&
            child.mediaTargetResolution
        ) {
            const childArea =
                child.mediaTargetResolution.width * child.mediaTargetResolution.height;

            // only consider this new child's color if its area is as large or larger than the current candidate color
            if (childArea >= bestColorArea) {
                if (VideoData.isMediaAsset(child)) {
                    // determine if this media asset completely covers any colors we're already seen
                    const isFullScreen = childArea === slideArea;
                    const isVisible = child.thumbnailUrl !== VideoData.INVISIBLE_IMAGE;

                    if (child.isEditable && isFullScreen && isVisible) {
                        // the current candidate background color is completely obscured by this media asset
                        backgroundColor = null;
                        bestColorArea = 0;
                        seenFullscreenEditableMedia = true;
                    } else if (seenFullscreenEditableMedia) {
                        // we've already encountered a media asset that consumes the entire slide area, but subsequent
                        // media assets can do fancy tricks with alpha channels that cause a color to appear on top of the
                        // otherwise full-screen media asset
                        for (const propName in templateReferenceMap[child.name]) {
                            const propValue = templateReferenceMap[child.name][propName];
                            if (colorNames.includes(propValue) && !propName.includes('Shadow')) {
                                backgroundColor = colorNames.indexOf(propValue);
                                bestColorArea = childArea;
                            }
                        }
                    }
                } else if (VideoData.isColorAsset(child) && !seenColorAsset) {
                    // find the first color property of this asset, because that's our current best estimate of the
                    // background color
                    for (const propName in templateReferenceMap[child.name]) {
                        const propValue = templateReferenceMap[child.name][propName];
                        if (
                            colorNames.includes(propValue) &&
                            !propName.includes('Shadow') &&
                            !propName.includes('Channel')
                        ) {
                            backgroundColor = colorNames.indexOf(propValue);
                            bestColorArea = childArea;
                            if (VideoData.isColorAsset(child)) {
                                seenColorAsset = true;
                            }
                        }
                    }
                }
            }
        }
    }

    return backgroundColor;
}

/**
 * Returns details about color usage for the given slide. The duplicateColors and relevantColors
 * properties of the returned object are arrays of indices into template.colors. The childColorProps property of the returned object
 * will look something like this:
 *
 * {
 *    'text_1': ['normalStyle.fill', 'colorGraphics.${index}.shape.fillColor', 'highlightStyle.fill'],
 *    'color_1': ['sourceColor', 'sourceAlpha'],
 *    ....
 * }
 */
export function extractSceneColors(
    slide: Scene,
    slideArea: number,
    invisibleAssetNames: string[],
    template: Template,
): {
    childColorProps: Record<string, Set<string>>;
    relevantColors: Set<number>;
    duplicateColors: Set<string>;
    backgroundColor: number | null;
    duotoneFilters: Set<DuotoneFilter>;
    luminanceOrder: Record<string, number>;
    colorNames: string[];
} {
    /* We need to figure out a few things:
        1. (childColorProps) Which slide children have color properties, and what are those color properties?
        2. (relevantColors) Which colors are relevant for shuffling? This is a set of color palette indices
        3. (duplicateColors) Which pairs of colors are the same color? This is a set of "i j" pair strings
        4. (backgroundColor) The color palette index of the scene's background color
        5. (duotoneFilters) This is a set of DuotoneFilter objects
        6. (luminanceOrder) How do the color palette colors rank in terms of luminance? This is a mapping from color name to integer, where color_i => 0 means color i is the darkest
        7. (colorNames) What's the reference name of each color palette color? This is an array of strings
    */

    const templateReferenceMap = slide.buildTemplateReferenceMap();
    const childColorProps: Record<string, Set<string>> = {};
    const relevantColors = new Set<number>();

    // track the number of text elements that have highlights of specific colors, and mark as irrelevant any colors mapped to
    // highlights that don't exist (e.g. if text_1 has a colors_2 highlight color, but there aren't any highlights in text_1, colors_2 isn't
    // useful)
    const highlightColorCounts: Record<string, number> = {};

    const colorPalette = new ColorPalette(template.colors);
    const highlightColorProps = new Set([TEXT_HIGHLIGHT_PROP]);
    const duplicateColors = colorPalette.findDuplicateColors();
    const colorNames = colorPalette.colorNames;
    const luminanceOrder = colorPalette.luminanceOrder;

    const children = slide.getAssets().filter((c: Asset) => c.name in templateReferenceMap);
    const backgroundColor = getBackgroundColor(slide, slideArea, colorNames);
    if (backgroundColor !== null) {
        relevantColors.add(backgroundColor);
    }

    // duotone filters have some properties we'll need to be careful about, so we'll collect them here as we find them
    const duotoneFilters = new Set<DuotoneFilter>();

    children.forEach((child) => {
        const templateProps = templateReferenceMap[child.name];

        childColorProps[child.name] = childColorProps[child.name] || new Set<string>();

        let duotoneFilter: DuotoneFilter | null = null;

        for (const propName in templateProps) {
            const propValue = templateProps[propName];

            const isHiddenAsset = invisibleAssetNames.includes(child.name);
            const isUsefulProp = !(
                isHiddenAsset ||
                propName.includes('Shadow') ||
                propName.includes('Alpha')
            );
            const isColorProp = colorNames.includes(propValue);

            if (isColorProp) {
                childColorProps[child.name].add(propName);
            }

            if (isUsefulProp && isColorProp) {
                if (highlightColorProps.has(propName)) {
                    // a color that's assigned to a highlight property is only valid if this asset actually has a text highlight
                    highlightColorCounts[propValue] = highlightColorCounts[propValue] || 0;
                    if (VideoData.isAnyTextAsset(child)) {
                        if (child.textHighlights.length > 0) {
                            relevantColors.add(colorNames.indexOf(propValue));
                            highlightColorCounts[propValue] += 1;
                        }
                    }
                } else if (propName.includes('filters')) {
                    // handle duotone filters
                    if (propName.includes('firstColor') || propName.includes('secondColor')) {
                        const color = template.colors[colorNames.indexOf(propValue)];
                        duotoneFilter = duotoneFilter || new DuotoneFilter();
                        duotoneFilter.addColorProp(propName, propValue, color.color);
                        relevantColors.add(colorNames.indexOf(propValue));
                    } else if (propName.includes('ChannelTargetColor')) {
                        relevantColors.add(colorNames.indexOf(propValue));
                    }
                } else {
                    // this is just a generic color property, so it's (probably) safe to say the color is relevant for shuffling
                    relevantColors.add(colorNames.indexOf(propValue));
                }
            }
        }

        if (duotoneFilter !== null) {
            duotoneFilters.add(duotoneFilter);
        }
    });

    return {
        childColorProps,
        relevantColors,
        duplicateColors,
        backgroundColor,
        duotoneFilters,
        luminanceOrder,
        colorNames,
    };
}

/**
 * Returns an object specifying the data needed to shuffle colors for a given slide (see ColorShuffleInfo definition for more info)
 */
export function getColorPermutations(
    slide: Scene,
    slideArea: number,
    invisibleAssetNames: string[],
    template: Template,
    maxShuffles = 6,
): ColorShuffleInfo {
    /**
     * HOW TO SHUFFLE COLORS
     * 1. compute a "contrast vector" capturing the contrast between each color and every other color in template.colors
     * 2. generate all possible permutations of the color palette
     * 3. filter out any permutations that don't yield any visible change
     * 4. compute contrast vectors for each remaining permutation
     * 5. compute cosine similarity between each permuted palette's contrast vector and the contrast vector of the original color palette
     * 6. select the color permutations whose contrast vectors are most similar to the original palette's contrast vector
     */

    // get all the scene data we need to make decisions about which color permutations are best
    const sceneColors = extractSceneColors(slide, slideArea, invisibleAssetNames, template);
    const colorNames = sceneColors.colorNames;

    /*
        Given a color permutation (for an n-color palette, this is some permutation of the array [1, 2, ..., n]), construct
        a new array that represents the contrast between all pairwise colors in the permutation:

        For a 3-color palette and the permutation [0, 1, 2], this array will look like this:

        [
            [0, [contrast(color 0, color 1), contrast(color 0, color 2)]],
            [1, [contrast(color 1, color 0), contrast(color 1, color 2)]],
            [2, [contrast(color 2, color 0), contrast(color 2, color 1)]]
        ]
    */
    const colorContrastVectorizer = (color_perm: number[]): [number, number[]][] => {
        return color_perm.map((c, i) => {
            let contrast_vec: number[] = [];
            for (let j = 0; j < i; j++) {
                contrast_vec = contrast_vec.concat(
                    Color.rgbContrast(
                        Color.rgb(template.colors[i].color),
                        Color.rgb(template.colors[j].color),
                    ),
                );
            }
            for (let k = i + 1; k < color_perm.length; k++) {
                contrast_vec = contrast_vec.concat(
                    Color.rgbContrast(
                        Color.rgb(template.colors[i].color),
                        Color.rgb(template.colors[k].color),
                    ),
                );
            }

            return [i, contrast_vec];
        });
    };

    /* Generate permutations of the color indices. Permutations of the color palette are expressed as integers. The un-permuted
       color palette looks like this: [0, 1, 2, 3, 4] (for a 4-color palette), and permutations are expressed like so:

       [0, 1, 3, 2]
       [0, 1, 3, 2] <- colors 2 and 3 in the original palette are swapped in this permutation
       [0, 2, 1, 3]
       [0, 2, 3, 1]
       ...
       [3, 2, 1, 0]

       In the permutation where colors 2 and 3 are swapped, any scene children attributes previously mapped to 'color_2' will be
       re-mapped to 'color_3' and vice-versa
    */
    const generateRandomPermutation = (arr: number[]): number[] => {
        const result = [...arr];
        // Fisher-Yates shuffle
        for (let i = result.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [result[i], result[j]] = [result[j], result[i]];
        }
        return result;
    };

    const validatePermutation = (permutationSoFar: number[]): [boolean, string] => {
        const key: string[] = [];

        // to ensure we have permutations that actually differ visually, we'll mask off colors we already know
        // aren't relevant and check whether we've already added a permutation that's identical with respect to the
        // useful color positions
        permutationSoFar.forEach((palette_idx, perm_idx) => {
            // palette_idx is color's position in the palette
            // perm_idx is the index of the color that's in the palette_idx position in this permutation
            if (sceneColors.relevantColors.has(perm_idx)) {
                key.push(`${palette_idx}`);
            } else {
                key.push('_');
            }
        });

        // does this permutation of the colors cause any duo-tone filters to be inverted?
        let preservesDuotoneFilters = true;
        sceneColors.duotoneFilters.forEach((duotoneFilter) => {
            if (
                !duotoneFilter.validateColorPermutation(
                    sceneColors.luminanceOrder,
                    permutationSoFar,
                    colorNames,
                )
            ) {
                preservesDuotoneFilters = false;
            }
        });

        const keyStr = key.toString();
        return [preservesDuotoneFilters, keyStr];
    };

    const permutator = (inputArr: number[]): number[][] => {
        const result: number[][] = [];
        const maskedPermutations = new Set();

        // For palettes with > 7 colors, use random sampling to avoid factorial explosion
        // 6! = 720 (manageable), 7! = 5,040 (slow), 8! = 40,320 (way too slow)
        const shouldUseSampling = inputArr.length > 6;
        const maxSamples = 750;

        if (shouldUseSampling) {
            // Random sampling approach for large palettes
            const attempts = new Set<string>();
            let attemptsCount = 0;
            const maxAttempts = maxSamples * 3; // Allow some failures due to duplicates/invalid perms

            while (result.length < maxSamples && attemptsCount < maxAttempts) {
                attemptsCount++;
                const perm = generateRandomPermutation(inputArr);
                const permKey = perm.toString();

                // Skip if we've already tried this exact permutation
                if (attempts.has(permKey)) continue;
                attempts.add(permKey);

                const [preservesDuotoneFilters, keyStr] = validatePermutation(perm);

                if (preservesDuotoneFilters && !maskedPermutations.has(keyStr)) {
                    result.push(perm);
                    maskedPermutations.add(keyStr);
                }
            }
        } else {
            // Exhaustive approach for small palettes (original algorithm)
            const permute = (remainingColors: number[], permutationSoFar: number[] = []) => {
                if (remainingColors.length === 0) {
                    // permutationSoFar is now the full permutation, so let's see if it's a good one
                    const [preservesDuotoneFilters, keyStr] = validatePermutation(permutationSoFar);

                    if (preservesDuotoneFilters && !maskedPermutations.has(keyStr)) {
                        result.push(permutationSoFar);
                        maskedPermutations.add(keyStr);
                    }
                } else {
                    for (let i = 0; i < remainingColors.length; i++) {
                        // grab the next unused color

                        const curr = remainingColors.slice();
                        // next is the index of the color we're about to add
                        const next = curr.splice(i, 1);

                        // abort this permutation if it swaps color indices that map to identical colors, because that isn't
                        // going to yield any visual difference

                        // colorIdx is the location where we'll be placing the index of the color we're about to add
                        const colorIdx = permutationSoFar.length;
                        const hasDuplicateSwap =
                            sceneColors.duplicateColors.has(`${colorIdx} ${next[0]}`) ||
                            sceneColors.duplicateColors.has(`${next[0]} ${colorIdx}`);
                        if (!hasDuplicateSwap) {
                            // add the new color index and continue building the permutation
                            permute(curr.slice(), permutationSoFar.concat(next));
                        }
                    }
                }
            };

            permute(inputArr);
        }

        return result;
    };

    const flatten = (indexedFeatureVector: [number, number[]][]): number[] => {
        // each permutation is an array of [color index, contrast vector]
        // we need to concatenate the vectors and throw away the colorIndices
        return ([] as number[]).concat(
            ...indexedFeatureVector.map((c) => {
                return c[1];
            }),
        );
    };

    const mostSimilar = (ref: [number, number[]][], options: [number, number[]][][], n: number) => {
        const ref_vec = flatten(ref);
        const similarities: [number, number][] = [];

        // get the similarity for each option against the reference vector (ref)
        options.forEach((opt, i) => {
            similarities.push([i, cosine_similarity(ref_vec, flatten(opt))]);
        });

        similarities.sort(function (a: [number, number], b: [number, number]) {
            return b[1] - a[1];
        });
        return similarities.slice(0, n);
    };

    const unshuffledColors = [...Array(template.colors.length).keys()];

    // generate all valid permutations of the palette colors, and discard the unshuffled permutation, because
    // we'll always want to include that one, and there's no reason to do ranking calculations on it
    const colorPermutations = permutator(unshuffledColors).filter(
        (perm) => !isEqual(perm, unshuffledColors),
    );

    // the first shuffle must be the non-shuffled colors
    let finalColorShuffles = [unshuffledColors];

    // if there's only one color permutation, it means no other permutations were deemed valid
    if (colorPermutations.length > 0) {
        // create feature vectors for determining good color shuffles
        const contrastPerms = colorPermutations.map((perm) => {
            return colorContrastVectorizer(perm);
        });

        // if there's a background color, we need to prefer switching that for a different color, so over-generate similar permutations and
        // filter them down to the permutations that have more background color variety
        const numCandidates =
            sceneColors.backgroundColor === null ? maxShuffles - 1 : 10 * maxShuffles;

        const mostSimilarPerms = mostSimilar(contrastPerms[0], contrastPerms, numCandidates);

        // the first element of each most_similar_perm is the index of the corresponding color permutation, so get each of those
        const mostSimilarColorPerms = mostSimilarPerms.map((perm) => colorPermutations[perm[0]]);

        if (numCandidates < maxShuffles) {
            finalColorShuffles = finalColorShuffles.concat(mostSimilarColorPerms);
        } else {
            // add the remaining final color shuffles, trying to maximize background color variety
            if (sceneColors.backgroundColor === null)
                throw new Error('sceneColors.backgroundColor cannot be null here');

            const shufflesByBgColor = [...Array(template.colors.length).keys()].map((colorIdx) => {
                // get the permutations in which the value at backgroundColorIndex is colorIdx
                return mostSimilarColorPerms.filter(
                    (perm) => perm[sceneColors.backgroundColor!] === colorIdx,
                );
            });

            /* shufflesByBgColor now looks like this:
               { 0: <array of arrays>, 1: <array of arrays>, 2: <array of arrays> ...}

               each key in shufflesByBgColor is an index into template.colors, and each array of arrays holds color palette index
               permutations where the key is the background color

               so shufflesByBgColor[0] = [ [0, 1, 2, 3, 4], [0, 2, 1, 3, 4], [0, 1, 3, 2, 4] ] means there are three color permutations
               that have color 0 as the background color
            */

            // to ensure we get good variety before background color repeats, start with the color two
            // colors away from the original background color (if we only started one color away, we'd have the first and last
            // color permutations with the same background color)
            let paletteColorIndex = (sceneColors.backgroundColor + 2) % template.colors.length;

            // we want to have background color variety between shuffles, so we'll grab permutations from each
            // entry in shufflesByBgColor in a round-robin fashion until we exhaust the candidates or reach
            // maxShuffles
            for (let i = 0; i < numCandidates; i++) {
                if (shufflesByBgColor[paletteColorIndex]) {
                    // pop the first permutation from the array
                    const permToAdd = shufflesByBgColor[paletteColorIndex].shift();

                    if (permToAdd) {
                        finalColorShuffles.push(permToAdd);
                    }
                }

                // do we have enough color permutations?
                if (finalColorShuffles.length === maxShuffles) {
                    break;
                }

                // keep going, and set up to grab from the next background color
                paletteColorIndex = (paletteColorIndex + 1) % template.colors.length;
            }
        }
    }

    const templateReferenceMap = slide.buildTemplateReferenceMap();

    return {
        colorShuffles: finalColorShuffles,
        childColorProps: sceneColors.childColorProps,
        templateReferenceMap,
    };
}

// Rank colors by "vividness" — how far from the black-white axis they are.
// Higher score means more interesting (saturated, mid-lightness). Uses HSL:
// distance-from-mid-lightness penalises near-black and near-white,
// saturation rewards chromatic colors over greys.
export const scoreVividness = (color: string): number => {
    const { s, l } = colord(color).toHsl();
    const lightnessFactor = 1 - Math.abs(l - 50) / 50;
    return (s / 100) * lightnessFactor;
};

export const pickMostInterestingColors = (colors: string[], max: number): string[] => {
    return colors
        .map((color, index) => ({ color, index, score: scoreVividness(color) }))
        .sort((a, b) => b.score - a.score)
        .slice(0, max)
        .sort((a, b) => a.index - b.index)
        .map((entry) => entry.color);
};
